/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { updateProgramLink } from "../../requesters/UsersRequester"
import { getCurrentUser, getBaseUrl } from "../../reducers"
import { APP_TITLE } from "../../util"

const SUPPORT_EMAIL = "support@performanceintelligence.com"
const CODE_LENGTH = 8

function LinkingCodePage({ baseUrl, currentUser }) {
  const [code, setCode] = useState("")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    document.title = APP_TITLE
  }, [])

  const isComplete = code.length === CODE_LENGTH

  const onSubmit = () => {
    if (!isComplete || submitting) return

    setSubmitting(true)
    setError("")

    updateProgramLink(baseUrl, code, currentUser).then(res => {
      // Successful enrolment returns HTTP 200 with a demographic_id payload.
      const succeeded = res && (res.status === 200 || res.response?.demographic_id || res.response?.status === "ok")
      if (succeeded) {
        window.location.href = "/overview"
      } else {
        setError("Invalid program code provided")
        setSubmitting(false)
      }
    }).catch(() => {
      setError("Invalid program code provided")
      setSubmitting(false)
    })
  }

  const onChange = e => {
    // Keep codes tidy: alphanumeric, uppercase, capped at the expected length
    const next = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, CODE_LENGTH)
    setCode(next)
    if (error) setError("")
  }

  const onKeyDown = e => {
    if (e.key === "Enter") onSubmit()
  }

  // The same /enrol page serves first-time enrolment and switching programs.
  // `is_program_active` tells us which one, so we can adapt the copy.
  const isEnrolled = Boolean(currentUser.is_program_active)
  const currentProgramName = currentUser.program_name || currentUser.active_enrolment?.program_name

  const heroSubtitle = isEnrolled
    ? "Enter a new program code below to move to a different program."
    : "Let's get you set up. Enter your unique program code below to begin."

  const supportLink = <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>

  let submitLabel = isEnrolled ? "Switch program" : "Continue"
  if (submitting) submitLabel = "Enrolling…"

  let noticeContent
  if (currentUser.signup_enrolment_failed) {
    noticeContent = (
      <>
        Your unique signup link was not valid. Please enter a program code below, or contact
        {" "}
        {supportLink}
        {" "}
        for help.
      </>
    )
  } else if (isEnrolled) {
    noticeContent = (
      <>
        You&apos;re currently enrolled in
        {" "}
        <strong>{currentProgramName}</strong>
        . Entering a new code below will switch you to a different program and replace your current one. Need help? Contact
        {" "}
        {supportLink}
        .
      </>
    )
  } else {
    noticeContent = (
      <>
        You don&apos;t have an enrolled program currently. If you have a program code, enter it below to get started. If you don&apos;t have one, please contact
        {" "}
        {supportLink}
        .
      </>
    )
  }

  return (
    <div className="linking-code-page-body">
      <div className="enrol-wrapper">
        <div className="enrol-hero">
          <div className="enrol-eyebrow">Performance Intelligence</div>
          {isEnrolled
            ? <h1>Switch program</h1>
            : (
              <h1>
                Welcome
                {currentUser.firstname ? `, ${currentUser.firstname}` : ""}
                {" "}
                👋
              </h1>
            )}
          <p>{heroSubtitle}</p>
        </div>

        <div className="enrol-card">
          <div className="enrol-notice">
            <span className="enrol-notice-icon">i</span>
            <span>{noticeContent}</span>
          </div>

          <label className="enrol-label" htmlFor="program-code">Unique program code</label>
          <input
            id="program-code"
            className={`form-control enrol-input${error ? " is-invalid" : ""}`}
            placeholder="e.g. AB12CD34"
            type="text"
            autoFocus
            value={code}
            onChange={onChange}
            onKeyDown={onKeyDown}
            aria-invalid={Boolean(error)} />

          <div className="enrol-hint">
            {error
              ? <span className="text-danger">{error}</span>
              : (
                <span>
                  Your code is
                  {" "}
                  {CODE_LENGTH}
                  {" "}
                  characters long.
                </span>
)}
            <span>
              {code.length}
              /
              {CODE_LENGTH}
            </span>
          </div>

          <button
            className="btn btn-main enrol-submit"
            type="button"
            onClick={onSubmit}
            disabled={!isComplete || submitting}>
            {submitLabel}
          </button>
        </div>

        <div className="enrol-footer">
          Need help? Contact
          {" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  currentUser: getCurrentUser(state),
  baseUrl: getBaseUrl(state)
})

export default connect(mapStateToProps)(LinkingCodePage)
